import type { LyrraOnePublicRegion } from '@/lib/lyrraone-public-region';
import { getMarketConfig, type LyrraOneCurrency } from '@/lib/lyrraone-market-config';

import type { FormationPublicSyllabus } from '@/lib/formation-syllabus';

export interface StoreFormationAuthor {
  id?: string;
  firstName?: string | null;
  lastName?: string | null;
}

export interface StoreFormationFlowMeta {
  id: string;
  title?: string;
  description?: string | null;
  estimatedDuration?: number | null;
  trainingType?: string | null;
  objectives?: unknown;
  creationMode?: 'simple' | 'advanced' | null;
  isDemo?: boolean;
  shortCodePublic?: string | null;
}

export interface StoreFormationListItem {
  id: string;
  title: string;
  description?: string | null;
  coverImageUrl?: string | null;
  category?: string | null;
  tags?: string[];
  language?: string | null;
  price: number;
  salesCount?: number;
  isFeatured?: boolean;
  user?: StoreFormationAuthor;
  flow?: StoreFormationFlowMeta;
}

export interface StoreFormationDetail extends StoreFormationListItem {
  flowId: string;
  syllabus?: FormationPublicSyllabus;
  flow?: StoreFormationFlowMeta & {
    objectives?: unknown;
  };
}

export interface StorePurchaseResult {
  sale: { id: string } | null;
  alreadyOwned: boolean;
  flowId: string;
}

export interface StorePurchaseStatus {
  owned: boolean;
  isOwner: boolean;
  flowId: string;
  price: number;
  purchasedAt?: string | null;
}

export function unwrapApiData<T>(resp: unknown): T {
  if (resp && typeof resp === 'object' && 'data' in resp) {
    return (resp as { data: T }).data;
  }
  return resp as T;
}

export function unwrapPaginatedList<T>(resp: unknown): T[] {
  if (resp && typeof resp === 'object' && 'data' in resp && Array.isArray((resp as { data: unknown }).data)) {
    return (resp as { data: T[] }).data;
  }
  if (Array.isArray(resp)) return resp as T[];
  return [];
}

export function formatAuthorName(user?: StoreFormationAuthor | null, fallback = 'Formateur'): string {
  if (!user) return fallback;
  const name = `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim();
  return name || fallback;
}

export function formatPriceEuros(cents: number): string {
  if (cents <= 0) return 'Gratuit';
  return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(cents / 100);
}

export function lyrraOneCurrencyForRegion(region: LyrraOnePublicRegion): LyrraOneCurrency {
  return getMarketConfig(region).currency;
}

export function formatLyrraOnePrice(
  cents: number,
  region: LyrraOnePublicRegion = 'intl',
  freeLabel = 'Gratuit',
): string {
  if (cents <= 0) return freeLabel;
  const market = getMarketConfig(region);
  return new Intl.NumberFormat(market.locale, {
    style: 'currency',
    currency: market.currency,
  }).format(cents / 100);
}

/** Prix marketplace LyrraOne — devise régionale + libellé gratuit traduit. */
export function formatStorePrice(
  cents: number,
  region: LyrraOnePublicRegion,
  freeLabel: string,
): string {
  return formatLyrraOnePrice(cents, region, freeLabel);
}

/** Libellé localisé d’un code langue formation (ex. `fr` → « français »). */
export function formatFormationLanguageLabel(
  raw: string | null | undefined,
  uiLocale = 'fr',
): string {
  if (!raw?.trim()) return '';
  const code = raw.split('-')[0].toLowerCase();
  try {
    const label = new Intl.DisplayNames([uiLocale], { type: 'language' }).of(code);
    return label ?? raw;
  } catch {
    return raw;
  }
}

function normalizeLangCode(raw: string | null | undefined, fallback = 'fr'): string {
  const code = raw?.trim().split('-')[0]?.toLowerCase();
  return code || fallback;
}

/** Meta « langue » : langue d’origine, ou traduction IA si le contenu affiché est traduit. */
export function resolveFormationLanguageDisplay(options: {
  sourceLanguage?: string | null;
  contentLanguage: string;
  labelLanguage: string;
  labelTranslation: string;
  aiTranslationTemplate: string;
}): { label: string; value: string } | null {
  const source = normalizeLangCode(options.sourceLanguage);
  const content = normalizeLangCode(options.contentLanguage, source);

  if (content !== source) {
    const langName = formatFormationLanguageLabel(content, options.contentLanguage);
    const value = options.aiTranslationTemplate.replace(/\{\{language\}\}/g, langName);
    return { label: options.labelTranslation, value };
  }

  const original = formatFormationLanguageLabel(source, options.contentLanguage);
  if (!original) return null;
  return { label: options.labelLanguage, value: original };
}

export function formatDurationMinutes(minutes?: number | null): string {
  if (!minutes || minutes <= 0) return '';
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h > 0 && m > 0) return `${h} h ${m} min`;
  if (h > 0) return `${h} h`;
  return `${m} min`;
}
